import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { 
  CreditCard, 
  Coins, 
  TrendingUp, 
  AlertTriangle, 
  Calendar,
  Download,
  Zap,
  CheckCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  Settings,
  Gift
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client.safe'

interface Transaction {
  id: string
  type: 'top_up' | 'usage' | 'refund'
  credits: number
  description: string
  date: string
  status: 'completed' | 'pending' | 'failed'
  case_id?: string
  stripe_receipt_url?: string
}

interface BillingInfo {
  remaining_credits: number
  monthly_included: number
  used_this_month: number
  auto_renew_enabled: boolean
  next_renewal_date?: string
  low_credit_threshold: number
  autorenew_enabled: boolean
  autorenew_threshold_credits: number
  autorenew_package_size: '1' | '10' | '100'
}

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface BillingPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const statusColors = {
  completed: 'bg-success/10 text-success border-success/20',
  pending: 'bg-warning/10 text-warning border-warning/20', 
  failed: 'bg-destructive/10 text-destructive border-destructive/20'
}

export function BillingPage({ organization, profile }: BillingPageProps) {
  const { t, i18n } = useTranslation()
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [autorenewEnabled, setAutorenewEnabled] = useState(false)
  const [thresholdCredits, setThresholdCredits] = useState(10)
  const [packageSize, setPackageSize] = useState<'1' | '10' | '100'>('10')
  const [trialInfo, setTrialInfo] = useState<{
    granted: boolean
    credits: number
    grantedAt: string | null
  } | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    fetchBillingInfo()
    fetchTransactions()
    fetchTrialInfo()
    checkAdminStatus()
  }, [])

  const fetchBillingInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('billing_accounts')
        .select('*')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single()
      
      if (error) throw error
      
      const billingInfo: BillingInfo = {
        remaining_credits: data?.current_credits || 0,
        monthly_included: 100,
        used_this_month: 100 - (data?.current_credits || 0),
        auto_renew_enabled: data?.autorenew_enabled || false,
        next_renewal_date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
        low_credit_threshold: data?.autorenew_threshold_credits || 10,
        autorenew_enabled: data?.autorenew_enabled || false,
        autorenew_threshold_credits: data?.autorenew_threshold_credits || 10,
        autorenew_package_size: '10'
      }
      
      setAutorenewEnabled(billingInfo.autorenew_enabled)
      setThresholdCredits(billingInfo.autorenew_threshold_credits)
      setPackageSize(billingInfo.autorenew_package_size)
      
      setBillingInfo(billingInfo)
    } catch (error) {
      console.error('Error fetching billing info:', error)
      toast({
        title: t("billing.toast.error"),
        description: t("billing.toast.errorDesc"),
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchTransactions = async () => {
    try {
      // Simulated transaction data with credits
      const mockTransactions: Transaction[] = [
        {
          id: 'txn_1',
          type: 'top_up',
          credits: 50,
          description: 'Påfyllning via Stripe',
          date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
          status: 'completed',
          stripe_receipt_url: 'https://pay.stripe.com/receipts/acct_1234/ch_1234'
        },
        {
          id: 'txn_2',
          type: 'usage',
          credits: -15,
          description: 'AI-analys (Premium modell)',
          date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
          status: 'completed',
          case_id: 'case_abc123'
        },
        {
          id: 'txn_3',
          type: 'usage',
          credits: -8,
          description: 'Dokumentanalys',
          date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
          status: 'completed',
          case_id: 'case_def456'
        },
        {
          id: 'txn_4',
          type: 'top_up',
          credits: 25,
          description: 'Månadsinkluderat (Pro Plan)',
          date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
          status: 'completed'
        }
      ]
      
      setTransactions(mockTransactions)
    } catch (error) {
      console.error('Error fetching transactions:', error)
    }
  }

  const fetchTrialInfo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: billingAccount } = await supabase
        .from('billing_accounts')
        .select('trial_granted, trial_credits, trial_granted_at')
        .eq('user_id', user.id)
        .single()

      if (billingAccount) {
        setTrialInfo({
          granted: billingAccount.trial_granted,
          credits: billingAccount.trial_credits,
          grantedAt: billingAccount.trial_granted_at
        })
      }
    } catch (error) {
      console.error('Error fetching trial info:', error)
    }
  }

  const checkAdminStatus = async () => {
    try {
      const { data } = await supabase.rpc('get_current_user_role')
      setIsAdmin(data === 'admin')
    } catch (error) {
      console.error('Error checking admin status:', error)
    }
  }

  const handleGrantTrialAgain = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase.rpc('admin_grant_trial_credits', {
        p_user_id: user.id,
        p_credits: 5
      })

      if (error) throw error

      const result = data as {
        success: boolean
        trial_credits: number
        message: string
      }

      if (result.success) {
        toast({
          title: "Trial-krediter tilldelade",
          description: `${result.trial_credits} nya trial-krediter har lagts till`
        })
        fetchTrialInfo()
        fetchBillingInfo() // Refresh billing info to show updated credits
      } else {
        toast({
          title: "Information",
          description: result.message,
          variant: "default"
        })
      }
    } catch (error) {
      console.error('Error granting trial:', error)
      toast({
        title: "Fel",
        description: "Kunde inte tilldela trial-krediter",
        variant: "destructive"
      })
    }
  }

  const addCredits = () => {
    toast({
      title: "Påfyllning",
      description: "Öppnar Stripe för påfyllning av krediter"
    })
  }

  const openStripePortal = () => {
    toast({
      title: "Stripe Portal",
      description: "Öppnar Stripe kundportal för att aktivera auto-förnyelse"
    })
  }

  const downloadInvoice = (transactionId: string) => {
    toast({
      title: "Laddar ner",
      description: "Kvitto laddas ner..."
    })
  }

  const viewCase = (caseId: string) => {
    toast({
      title: "Öppnar ärende",
      description: `Navigerar till ärende ${caseId}`
    })
  }

  const handleAutorenewToggle = (enabled: boolean) => {
    setAutorenewEnabled(enabled)
    toast({
      title: enabled ? "Automatisk påfyllning aktiverad" : "Automatisk påfyllning inaktiverad",
      description: enabled ? "Ditt saldo kommer fyllas på automatiskt när det blir lågt" : "Du behöver fylla på ditt saldo manuellt"
    })
  }

  const handleThresholdChange = (value: string) => {
    const numValue = parseInt(value) || 0
    setThresholdCredits(numValue)
  }

  const handlePackageSizeChange = (value: '1' | '10' | '100') => {
    setPackageSize(value)
  }

  const saveAutorenewSettings = () => {
    toast({
      title: "Inställningar sparade",
      description: `Automatisk påfyllning: ${autorenewEnabled ? 'På' : 'Av'}, Tröskel: ${thresholdCredits} krediter, Paket: ${packageSize} krediter`
    })
  }

  const managePaymentMethod = () => {
    toast({
      title: "Stripe Portalen",
      description: "Öppnar Stripe kundportal för att hantera betalmetoder"
    })
  }

  const formatCredits = (credits: number) => {
    return `${credits} krediter`
  }

  const formatDate = (dateString: string) => {
    const locale = i18n.language?.startsWith('sv') ? 'sv-SE' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getUsagePercentage = () => {
    if (!billingInfo) return 0
    return (billingInfo.used_this_month / billingInfo.monthly_included) * 100
  }

  const isLowCredits = () => {
    if (!billingInfo) return false
    return billingInfo.remaining_credits < billingInfo.low_credit_threshold
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!billingInfo) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Kunde inte ladda faktureringsinformation</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('billing.title.page')}</h1>
          <p className="text-muted-foreground">{t('billing.subtitle.page')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={addCredits} className="gap-2">
            <CreditCard className="h-4 w-4" />
            {t('billing.topUp')}
          </Button>
        </div>
      </div>

      {/* Low Credits Alert */}
      {isLowCredits() && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t('billing.alert.lowCredits', { credits: billingInfo.remaining_credits })}
          </AlertDescription>
        </Alert>
      )}

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('billing.stats.remaining')}</CardTitle>
            <Coins className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {billingInfo.remaining_credits}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('billing.stats.remainingDesc')}
            </p>
        </CardContent>
      </Card>

      {/* Trial Credits Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            {t('billing.trial.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trialInfo ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <div className="font-medium">{t('billing.trial.status')}</div>
                  <div className="text-sm text-muted-foreground">
                    {trialInfo.granted ? t('billing.trial.statusGranted') : t('billing.trial.statusNotGranted')}
                  </div>
                </div>
                <Badge variant={trialInfo.granted ? 'default' : 'secondary'}>
                  {trialInfo.granted ? t('billing.trial.credits', { count: trialInfo.credits }) : t('billing.trial.none')}
                </Badge>
              </div>
              
              {trialInfo.granted && trialInfo.grantedAt && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {t('billing.trial.grantedAt')} {new Date(trialInfo.grantedAt).toLocaleDateString(i18n.language?.startsWith('sv') ? 'sv-SE' : 'en-US', {
                    year: 'numeric',
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
              
              {isAdmin && (
                <div className="pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleGrantTrialAgain}
                    className="gap-2"
                  >
                    <Gift className="h-4 w-4" />
                    {t('billing.trial.grantAgainAdmin')}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('billing.trial.grantAgainNote')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              {t('billing.trial.loading')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('billing.usageThisMonth')}</CardTitle>
            <Zap className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {billingInfo.used_this_month} / {billingInfo.monthly_included}
            </div>
            <div className="space-y-1 mt-2">
              <Progress value={getUsagePercentage()} className="w-full" />
              <p className="text-xs text-muted-foreground">
                {t('billing.usageOfIncluded')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {billingInfo.auto_renew_enabled ? t('billing.autorenew.next') : t('billing.autorenew.title')}
            </CardTitle>
            {billingInfo.auto_renew_enabled ? (
              <CheckCircle className="h-4 w-4 text-success" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground" />
            )}
          </CardHeader>
          <CardContent>
            {billingInfo.auto_renew_enabled ? (
              <div>
                <div className="text-2xl font-bold">
                  {billingInfo.next_renewal_date ? 
                    formatDate(billingInfo.next_renewal_date) : 
                    'N/A'
                  }
                </div>
                <p className="text-xs text-success">
                  {t('billing.autorenew.active')}
                </p>
              </div>
            ) : (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {t('billing.autorenew.inactive')}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={openStripePortal}
                  className="gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('billing.autorenew.enable')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Auto-Refill Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle>{t('billing.autorenew.sectionTitle')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-base font-medium">{t('billing.autorenew.enableLabel')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('billing.autorenew.enableDesc')}
              </p>
            </div>
            <Switch 
              checked={autorenewEnabled} 
              onCheckedChange={handleAutorenewToggle}
            />
          </div>

          {autorenewEnabled && (
            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="threshold">{t('billing.autorenew.thresholdLabel')}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="threshold"
                    type="number"
                    value={thresholdCredits}
                    onChange={(e) => handleThresholdChange(e.target.value)}
                    className="w-24"
                    min="1"
                    max="100"
                  />
                  <span className="text-sm text-muted-foreground">{t('billing.autorenew.creditsSuffix')}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Label>{t('billing.autorenew.packageSizeLabel')}</Label>
                <RadioGroup value={packageSize} onValueChange={handlePackageSizeChange}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1" id="package-1" />
                    <Label htmlFor="package-1">1 {t('billing.autorenew.creditsSuffix')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="10" id="package-10" />
                    <Label htmlFor="package-10">10 {t('billing.autorenew.creditsSuffix')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="100" id="package-100" />
                    <Label htmlFor="package-100">100 {t('billing.autorenew.creditsSuffix')}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={saveAutorenewSettings} variant="default">
                  {t('billing.autorenew.save')}
                </Button>
                <Button onClick={managePaymentMethod} variant="outline" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  {t('billing.autorenew.managePayment')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.table.history')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.table.type')}</TableHead>
                <TableHead>{t('billing.table.description')}</TableHead>
                <TableHead>{t('billing.table.date')}</TableHead>
                <TableHead>{t('billing.table.status')}</TableHead>
                <TableHead className="text-right">{t('billing.table.credits')}</TableHead>
                <TableHead className="text-right">{t('billing.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {t('billing.table.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {transaction.type === 'top_up' && <Coins className="h-4 w-4 text-success" />}
                        {transaction.type === 'usage' && <Zap className="h-4 w-4 text-warning" />}
                        {transaction.type === 'refund' && <RefreshCw className="h-4 w-4 text-primary" />}
                        <span className="capitalize">
                          {transaction.type === 'top_up' ? t('billing.table.type.top_up') :
                           transaction.type === 'usage' ? t('billing.table.type.usage') : t('billing.table.type.refund')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>{formatDate(transaction.date)}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[transaction.status]}>
                        {transaction.status === 'completed' ? t('billing.table.status.completed') :
                         transaction.status === 'pending' ? t('billing.table.status.pending') : t('billing.table.status.failed')}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${
                      transaction.credits > 0 ? 'text-success' : 'text-destructive'
                    }`}>
                      {transaction.credits > 0 ? '+' : ''}{transaction.credits}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {transaction.stripe_receipt_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(transaction.stripe_receipt_url, '_blank')}
                            className="gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t('billing.table.receipt')}
                          </Button>
                        )}
                        {transaction.case_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewCase(transaction.case_id!)}
                            className="gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t('billing.table.case')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}